import { useContext, useEffect, useId, useMemo, useRef } from 'react';
import { useIsFocused } from 'expo-router';
import { useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { GuideContext } from '@/components/Guides/GuideProvider';
import { getGuide, GUIDES_ENABLED } from '@/config/guides';

// Shared empty array: callers commonly destructure `data = []`, producing a
// new [] every render while the query loads — which would re-register the
// page in a loop (register → provider setPage → consumer rerender → new []).
const EMPTY_LIST = [];

export default function useGuide(guideID, { ready = true, forms, myEntries, disabled = false, scrollToTarget, extra } = {}) {
    const { data: user } = Query.people.loggedInUser();
    const { rt: { isLandscape } } = useUnistyles();
    const focused = useIsFocused();
    const id = useId();
    const visit = useRef(0);
    const guide = useContext(GuideContext);
    const scroll = useRef(scrollToTarget);
    scroll.current = scrollToTarget;
    // `extra` lets a page expose live state (view mode, row counts) for step
    // eligibility. Keyed on its serialized value so callers can pass inline
    // objects without destabilizing registration.
    const extraKey = JSON.stringify(extra || null);
    const formsStable = forms?.length ? forms : EMPTY_LIST;
    const entriesStable = myEntries?.length ? myEntries : EMPTY_LIST;
    const context = useMemo(() => ({ user, organizationID: user?.organizationID, isLandscape, forms: formsStable, myEntries: entriesStable, ...(extraKey ? JSON.parse(extraKey) : {}) }), [user, isLandscape, formsStable, entriesStable, extraKey]);
    const register = guide?.registerPage;
    const unregister = guide?.unregisterPage;
    const resetVersion = guide?.resetVersion;
    const enabled = GUIDES_ENABLED && !!getGuide(guideID);
    useEffect(() => {
        if (!focused) return;
        visit.current += 1;
    }, [focused]);
    useEffect(() => {
        if (!focused || !register || disabled || !enabled) return;
        const pageID = `${id}:${visit.current}`;
        register({ id: pageID, guideID, context, ready: ready && !!user?.id, scrollToTarget: targetID => scroll.current?.(targetID) });
        return () => unregister(pageID);
    }, [id, focused, register, unregister, guideID, context, ready, disabled, user?.id, resetVersion, enabled]);
    return enabled ? guide : null;
}
